'use client';

import React from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { useResponsive } from '@depop/web-ui-kit/useResponsive';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import {
  ACTIVE_OFFERS_LG_SCREEN_MAX_ITEMS,
  ACTIVE_OFFERS_MD_SCREEN_MAX_ITEMS,
} from '@/app/[locale]/(home)/components/ActiveOffers/constants';
import { ExtendedLink } from '@/components/ExtendedLink';
import { ModuleHeader, ModuleHeaderSize } from '@/components/ModuleHeader';
import { OfferTile } from '@/components/OfferTile';
import {
  ActivityTrackerEventType,
  HomePageModuleNames,
  ModuleInteractionType,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { getActiveOffers } from '@/modules/offers/buyers/helpers/getActiveOffers';
import { useBuyersActiveOffers } from '@/modules/offers/buyers/hooks/useBuyersActiveOffers';
import { BuyerOfferDisplayStatus } from '@/modules/offers/buyers/types';
import { useRecentlyViewedProducts } from '@/modules/recentlyViewed/hooks/useRecentlyViewedProducts';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function ActiveOffersUI() {
  const tOffers = useTranslations('offers');
  const tCommon = useTranslations('common');
  const [_, currentUser] = useCurrentUser();
  const { isMinLarge, isMinMedium } = useResponsive();
  const {
    products: recentlyViewedProducts,
    refetch: refetchRecentlyViewedProducts,
  } = useRecentlyViewedProducts();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });

  const { data: offersData, refetch } = useBuyersActiveOffers();
  const activeOffers =
    (offersData?.pages && getActiveOffers(offersData.pages[0])) || [];

  function handleSeeMoreClick() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MODULE_INTERACTION_ACTION,
      {
        moduleName: HomePageModuleNames.ActiveOffers,
        interactionType: ModuleInteractionType.MakeOfferHomePageSeeMore,
        schemaVersion: '1.0',
      }
    );
  }

  const isMediumScreen = isMinMedium && !isMinLarge;
  const hasDismissibleOffer = activeOffers.some(
    (offer) =>
      offer.offer_display_status ===
      BuyerOfferDisplayStatus.RECEIVED_PRIVATE_OFFER
  );

  const visibleActiveOffers = activeOffers.slice(
    0,
    isMediumScreen
      ? ACTIVE_OFFERS_MD_SCREEN_MAX_ITEMS
      : ACTIVE_OFFERS_LG_SCREEN_MAX_ITEMS
  );

  function handleSendOffer(productId: number) {
    if (recentlyViewedProducts.some((prd) => prd.id === productId)) {
      refetchRecentlyViewedProducts();
    }
  }

  return (
    <section>
      <ModuleHeader
        title={tOffers('YourActiveOffers')}
        size={ModuleHeaderSize.Large}
      >
        <ExtendedLink href="/messages/offers/" onClick={handleSeeMoreClick}>
          <Body variant="sm-regular" className={styles.seeMoreText}>
            {tCommon('SeeMore')}
          </Body>
        </ExtendedLink>
      </ModuleHeader>

      <ul className={styles.activeOffersContainer}>
        {visibleActiveOffers.map((offerItem) => (
          <li key={offerItem.offer_id} className={styles.activeOfferListItem}>
            <OfferTile
              {...offerItem}
              onCounterOfferSent={() => handleSendOffer(offerItem.product_id)}
              showInlineButtons
              includeBreakpointStyles={false}
              showImageMarginTop={hasDismissibleOffer}
              refetchParentQuery={refetch}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
